import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Settings, Wrench, LineChart, Flag, Bike, ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { IMAGES, WORK_STEPS } from "../mock";
import { api } from "../lib/api";

const iconMap = { Settings, Wrench, LineChart, Flag, Bike };

const Services = () => {
  const { t, lang } = useLanguage();
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.listServices().then(setServices).catch(() => setServices([]));
  }, []);

  const title = (s) => (lang === "en" && s.title_en ? s.title_en : s.title_pt);
  const desc = (s) => (lang === "en" && s.desc_en ? s.desc_en : s.desc_pt);

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-40 pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.servicesHero} alt="Services hero" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#070b18]/85 via-[#070b18]/85 to-[#070b18]" />
        </div>
        <div className="relative z-10 text-center max-w-3xl mx-auto bf-section-fade">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4">{t("sp_title")}</h1>
          <p className="text-lg md:text-xl text-gray-300">{t("sp_subtitle")}</p>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-20 px-6 bg-[#0a1024]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => {
            const Icon = iconMap[s.icon] || Settings;
            return (
              <div key={s.id} className="bf-glass bf-card-hover rounded-2xl p-8">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#22d3ee]/20 to-[#22d3ee]/5 border border-[#22d3ee]/30 flex items-center justify-center mb-6">
                  <Icon className="w-7 h-7 text-[#22d3ee]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{title(s)}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc(s)}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How we work */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white text-center mb-16">{t("how_title")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {WORK_STEPS.map((step) => (
              <div key={step.num} className="relative">
                <div className="text-6xl font-extrabold text-white/5 mb-3">{step.num}</div>
                <div className="absolute top-2 left-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#22d3ee] to-[#0ea5c4] flex items-center justify-center text-[#0a1024] font-bold text-sm">
                  {step.num}
                </div>
                <h3 className="text-lg font-bold text-white mb-2 mt-2">{step.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-16">
            <Link to="/booking" className="bf-btn-primary text-white font-semibold px-10 py-4 rounded-full inline-flex items-center gap-2 uppercase tracking-wide text-sm">
              {t("cta_book")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
